import { formatToDateAndTime } from '@zendesk/forethought-elements';
import {
  AutoflowVersionHistorySummary,
  RestorationRequirements,
} from 'src/types/workflowBuilderAPITypes';

// Trailing ISO-8601 timezone designator: 'Z' or a numeric offset
// (+05, +0530, or +05:30).
const TIMEZONE_DESIGNATOR = /(Z|[+-]\d{2}(:?\d{2})?)$/;

/**
 * Formats a version timestamp for display, tagging it as UTC (with 'Z') when the
 * backend leaves the timezone off so it shows in the user's local time instead of
 * appearing hours off.
 */
export const formatVersionTimestamp = (
  timestamp: string | null | undefined,
): string => {
  if (!timestamp) {
    return '';
  }
  const isUtcMarked = TIMEZONE_DESIGNATOR.test(timestamp);
  const normalized = isUtcMarked ? timestamp : `${timestamp}Z`;
  if (Number.isNaN(new Date(normalized).getTime())) {
    return '';
  }
  return formatToDateAndTime(normalized);
};

/**
 * Returns the display name for a version.
 * Auto-saved versions show "Auto-Saved" as the display name.
 */
export const getVersionDisplayName = (
  version: AutoflowVersionHistorySummary | undefined,
): string | undefined => {
  if (!version) {
    return undefined;
  }

  if (version.version_type === 'auto_saved') {
    return 'Auto-Saved';
  }

  return version.version_name;
};

/**
 * Checks if a version is an auto-saved version.
 */
export const isAutoSavedVersion = (
  version: AutoflowVersionHistorySummary | undefined,
): boolean => {
  return version?.version_type === 'auto_saved';
};

export type VersionDisplayState = 'live' | 'offline' | 'auto-saved';

/**
 * Returns the display state for a version based on its publish status.
 * - 'live': version is currently published (is_published === true)
 * - 'offline': version was published but is no longer live
 * - 'auto-saved': auto-saved or saved version (not published)
 */
export const getVersionDisplayState = (
  version: AutoflowVersionHistorySummary | null | undefined,
): VersionDisplayState => {
  if (!version) {
    return 'auto-saved';
  }

  if (version.is_published === true) {
    return 'live';
  }

  if (version.version_type === 'published' && version.is_published === false) {
    return 'offline';
  }

  return 'auto-saved';
};

/**
 * Filters versions that should appear in the "Versions" section.
 * Includes saved versions and auto-saved versions when there's a published version.
 */
export const filterNamedVersions = (
  versionHistory: AutoflowVersionHistorySummary[],
  publishedVersions: AutoflowVersionHistorySummary[],
): AutoflowVersionHistorySummary[] => {
  const hasPublishedVersion = publishedVersions.length > 0;

  return versionHistory.filter(
    v =>
      // Include saved versions or auto-saved versions when published exists
      v.version_type === 'saved' ||
      (v.version_type === 'auto_saved' && hasPublishedVersion),
  );
};

/**
 * Checks if a version name already exists in the version history (case-insensitive).
 * Skips empty and "None" names. Optionally excludes a specific version number
 * (useful when renaming a version to allow keeping its own name).
 */
export const isVersionNameDuplicate = (
  versionHistory: AutoflowVersionHistorySummary[],
  name: string,
  excludeVersion?: number,
): boolean => {
  const trimmed = name.trim();
  if (!trimmed || trimmed.toLowerCase() === 'none') {
    return false;
  }
  return versionHistory.some(
    v =>
      v.version !== excludeVersion &&
      v.version_name &&
      v.version_name.toLowerCase() !== 'none' &&
      v.version_name.toLowerCase() === trimmed.toLowerCase(),
  );
};

/**
 * Checks if any item in restoration requirements has can_restore: false.
 * Returns true if restoration is blocked (at least one item cannot be restored).
 */
export const hasUnrestorableItems = (
  requirements: RestorationRequirements,
): boolean => {
  const { deleted_actions, deleted_child_workflows, modified_actions } =
    requirements;

  return (
    deleted_actions.some(item => item.can_restore === false) ||
    deleted_child_workflows.some(item => item.can_restore === false) ||
    modified_actions.some(item => item.can_restore === false)
  );
};

/**
 * Returns arrays of items that cannot be restored (can_restore: false).
 */
export const getUnrestorableItems = (
  requirements: RestorationRequirements,
): {
  actions: typeof requirements.deleted_actions;
  childWorkflows: typeof requirements.deleted_child_workflows;
  modifiedActions: typeof requirements.modified_actions;
} => {
  const { deleted_actions, deleted_child_workflows, modified_actions } =
    requirements;

  return {
    actions: deleted_actions.filter(item => item.can_restore === false),
    childWorkflows: deleted_child_workflows.filter(
      item => item.can_restore === false,
    ),
    modifiedActions: modified_actions.filter(
      item => item.can_restore === false,
    ),
  };
};
